/**
 * Touch-swipe hook (PHASE1 §7.2). Returns pointer/touch handlers to spread onto
 * an element; a horizontal drag past `threshold` px (that stays mostly
 * horizontal) fires `onSwipeLeft` / `onSwipeRight`.
 */
import { useRef, type TouchEvent } from 'react';

export interface SwipeHandlers {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  /** Minimum horizontal distance in px to count as a swipe. Default 60. */
  threshold?: number;
}

export interface SwipeBinding {
  onTouchStart: (e: TouchEvent) => void;
  onTouchMove: (e: TouchEvent) => void;
  onTouchEnd: (e: TouchEvent) => void;
}

export function useSwipe({
  onSwipeLeft,
  onSwipeRight,
  threshold = 60,
}: SwipeHandlers): SwipeBinding {
  const startRef = useRef<{ x: number; y: number } | null>(null);

  return {
    onTouchStart: (e: TouchEvent) => {
      const t = e.touches[0];
      startRef.current = { x: t.clientX, y: t.clientY };
    },
    onTouchMove: () => {
      // Tracking happens on end; nothing to do mid-move.
    },
    onTouchEnd: (e: TouchEvent) => {
      const origin = startRef.current;
      startRef.current = null;
      if (!origin) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - origin.x;
      const dy = t.clientY - origin.y;
      if (Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy)) return;
      if (dx < 0) onSwipeLeft?.();
      else onSwipeRight?.();
    },
  };
}
